// Pure scheduler bookkeeping for curvature-at-junction arc derivation.
// No OpenLayers / external imports — unit-tested with `node --test`.
//
// State per source: { derivedIds: Set, deferredIds: Set }
//   derivedIds  — relations already turned into an arc (committed). Never redone.
//   deferredIds — relations seen but missing >=1 member. Parked: NOT retried every
//                 frame; only re-armed (cleared) by the next featuresloadend / clear.

// Mirrors getTags()'s key parsing without importing it (keeps this module dep-free):
// a property named "<digits>-curvature_at_junction" means the relation carries the tag.
function hasCurvatureAtJunctionTag(feature) {
  const props = feature.getProperties();
  return Object.keys(props).some((p) => {
    const m = p.match(/^(\d+)-(.+)$/);
    return m && m[2] === "curvature_at_junction";
  });
}

export function selectCandidates(features, derivedIds, deferredIds) {
  return features.filter((f) => {
    if (f.get("type") !== "ORBIS_RELATION") return false;
    const id = f.getId();
    if (derivedIds.has(id) || deferredIds.has(id)) return false;
    return hasCurvatureAtJunctionTag(f);
  });
}

export function buildIndex(features) {
  const map = new Map();
  for (const f of features) {
    const id = f.getId();
    if (id !== undefined) map.set(id, f);
  }
  return map;
}

/**
 * Process the source's outstanding candidates, time-sliced.
 *   deriveArc(relation, indexById) -> { status:"derived", feature } | { status:"missing" }
 *   deadline / now() — pacing; the deadline is checked BETWEEN relations.
 * Each candidate is resolved exactly once: committed (derivedIds) or parked
 * (deferredIds). Returns { done } — true when this pass exhausted its candidate
 * snapshot (all were processed before the deadline), false when the deadline
 * fired mid-pass (the remaining candidates will be picked up on the next pass).
 */
export function runDerivationPass(source, state, { deriveArc, deadline, now }) {
  const features = source.getFeatures();
  const indexById = buildIndex(features);
  const candidates = selectCandidates(
    features,
    state.derivedIds,
    state.deferredIds,
  );
  for (let i = 0; i < candidates.length; i++) {
    const relation = candidates[i];
    const result = deriveArc(relation, indexById);
    if (result.status === "derived") {
      source.addFeature(result.feature);
      state.derivedIds.add(relation.getId());
    } else {
      state.deferredIds.add(relation.getId());
    }
    if (now() >= deadline) {
      return { done: i === candidates.length - 1 };
    }
  }
  return { done: true };
}
